/**
 * Login.tsx — Acervo | Galeria Leme
 *
 * Design idêntico ao Portal CV (portal/static/index.html).
 * Diferença: subtítulo "ACERVO" em vez de "PORTAL CV".
 *
 * Suporta dois modos:
 * - Firebase Auth (produção)
 * - Stub mode apenas em DEV com VITE_ALLOW_STUB_AUTH=true
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "@/lib/firebase";
import { useAuth } from "@/hooks/useAuth";
import "./Login.css";

export default function Login() {
  const navigate = useNavigate();
  const { stubLogin, isStub, authError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "error" | "success" | null; text: string }>({ type: null, text: "" });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: null, text: "" });

    if (!email) {
      setMessage({ type: "error", text: "Preencha o email." });
      setLoading(false);
      return;
    }

    if (isStub) {
      // Stub mode: accept any email/password, create stub user
      stubLogin(email);
      navigate("/dashboard");
      return;
    }

    if (!auth) {
      setMessage({
        type: "error",
        text:
          authError ??
          "Autenticação indisponível. Verifique a configuração do Firebase.",
      });
      setLoading(false);
      return;
    }

    try {
      const { signInWithEmailAndPassword } = await import("firebase/auth");
      await signInWithEmailAndPassword(auth!, email, password);
      navigate("/dashboard");
    } catch (err: unknown) {
      const code = (err as { code?: string }).code || "";
      const messages: Record<string, string> = {
        "auth/invalid-credential": "Email ou senha incorretos.",
        "auth/wrong-password": "Email ou senha incorretos.",
        "auth/user-not-found": "Usuário não encontrado.",
        "auth/too-many-requests": "Muitas tentativas. Tente novamente em alguns minutos.",
        "auth/invalid-email": "Endereço de email inválido.",
        "auth/user-disabled": "Esta conta foi desativada.",
      };
      setMessage({ type: "error", text: messages[code] || "Erro ao fazer login. Tente novamente." });
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    setMessage({ type: null, text: "" });
    if (!email) {
      setMessage({ type: "error", text: "Preencha o email para redefinir a senha." });
      return;
    }
    if (!auth) {
      setMessage({
        type: "error",
        text:
          authError ??
          "Autenticação indisponível. Verifique a configuração do Firebase.",
      });
      return;
    }
    try {
      const { sendPasswordResetEmail } = await import("firebase/auth");
      await sendPasswordResetEmail(auth, email);
      setMessage({ type: "success", text: `Email de redefinição enviado para ${email}` });
    } catch {
      setMessage({ type: "error", text: "Erro ao enviar email. Tente novamente." });
    }
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-box__logo">
          <img src="/assets/logos/logo.svg" alt="Galeria Leme" className="login-box__logo-img" style={{ height: 320, width: "auto" }} />
        </div>
        <div className="login-box__subtitle">Acervo</div>

        {message.type && (
          <div className={`login-message login-message--${message.type}`} role={message.type === "error" ? "alert" : "status"}>
            {message.text}
          </div>
        )}

        {!isStub && authError && !message.type && (
          <div className="login-message login-message--error" role="alert">
            {authError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="login-form__group">
            <label className="login-form__label" htmlFor="email">Email</label>
            <input className="login-form__input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus />
          </div>
          <div className="login-form__group">
            <label className="login-form__label" htmlFor="password">Senha</label>
            <input className="login-form__input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          <button type="submit" className="login-form__submit" disabled={loading}>
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>

        <button type="button" className="login-forgot" onClick={handleForgotPassword}>
          Esqueci minha senha
        </button>
      </div>
    </div>
  );
}
